// Cotización con reparto: un solo bloque «El crédito» con sus montos, el total y la promo de crédito.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { PromoChip } from './PromoChip';

/** Tasas y montos sin la promoción; `null` cuando no hay promoción de tasa. */
export interface QuotePromoView {
    baseTnaPct: number;
    appliedTnaPct: number;
    installmentsWithoutCents: number;
    /** Cuánto baja cada cuota con la promoción. */
    weeklySavingCents: number;
}

export interface QuoteSplitNumbers {
    downPaymentCents: number;
    cashCents: number;
    financedCents: number;
    /** `null` sin comisión de apertura. */
    openingFee: { pct: number; cents: number } | null;
    interestCents: number;
    ivaCents: number;
    weeklyCents: number;
    termWeeks: number;
    installmentsCents: number;
    promo: QuotePromoView | null;
}

function Line({
    label,
    value,
    className,
}: {
    label: ReactNode;
    value: ReactNode;
    className?: string;
}) {
    return (
        <div data-row className={cn('flex justify-between gap-3', className)}>
            <span>{label}</span>
            <span className="tabular-nums">{value}</span>
        </div>
    );
}

const CARD = 'flex flex-col gap-2 rounded-[10px] border border-border bg-card p-4 text-[12.5px]';
const TOTAL_LINE = 'border-t border-border pt-2 font-semibold';

function mxn(cents: number): string {
    return formatMXN(cents / 100);
}

/** Todas las cuotas son iguales, con IVA incluido. */
function installmentsLabel(n: QuoteSplitNumbers): string {
    return `${String(n.termWeeks)} cuotas fijas de ${mxn(n.weeklyCents)}`;
}

export function QuoteSplitSummary({ n }: { n: QuoteSplitNumbers }) {
    const todayCents = n.downPaymentCents + n.cashCents;
    const savingCents =
        n.promo === null ? 0 : Math.max(0, n.promo.installmentsWithoutCents - n.installmentsCents);
    return (
        <section data-testid="quote-credit" aria-labelledby="quote-credit-title" className={CARD}>
            <h2 id="quote-credit-title" className="text-[13.5px] font-bold">
                El crédito · {n.termWeeks} semanas
            </h2>
            <Line label="Monto a financiar" value={mxn(n.financedCents)} />
            {n.openingFee !== null && (
                <Line
                    label={`Comisión de apertura (${String(n.openingFee.pct)}%)`}
                    value={mxn(n.openingFee.cents)}
                />
            )}
            <Line label="Intereses" value={mxn(n.interestCents)} />
            <Line label="IVA sobre intereses (16%)" value={mxn(n.ivaCents)} />
            <Line label="Total en cuotas" value={mxn(n.installmentsCents)} className={TOTAL_LINE} />
            <p className="text-[11.5px] text-muted-foreground">{installmentsLabel(n)}</p>
            <div
                data-testid="quote-pays-total"
                className="flex flex-col gap-0.5 border-t border-border pt-2"
            >
                {/* La cifra más visible del bloque; en angosto el monto baja a su propia línea. */}
                <div data-row className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <span className="text-[12.5px] font-semibold">El cliente paga en total</span>
                    <span
                        data-testid="quote-pays-total-amount"
                        className="text-[30px] font-bold leading-tight tabular-nums"
                    >
                        {mxn(todayCents + n.installmentsCents)}
                    </span>
                </div>
                <p className="text-[11.5px] tabular-nums text-muted-foreground">
                    hoy {mxn(todayCents)} + cuotas {mxn(n.installmentsCents)}
                </p>
            </div>
            {n.promo !== null && (
                <div data-testid="quote-credit-promo" className="flex">
                    <PromoChip size="md">
                        Promo · tasa {n.promo.baseTnaPct}% → {n.promo.appliedTnaPct}% · ahorra{' '}
                        {mxn(n.promo.weeklySavingCents)} por cuota · {mxn(savingCents)} en total
                    </PromoChip>
                </div>
            )}
        </section>
    );
}
